import { IIconProps, useTheme } from '@fluentui/react';
import { IPDefaultButton } from 'baseComponents';
import { globalFontBoldWeight, startIcon, stopIcon } from 'globalStyles';
import { CopilotAction, CopilotActionType } from 'models/copilot/copilotAction';
import { TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { isFinishedProcessStatus } from './Conversation/copilotActionState';

type CopilotPanelButtonProps = {
  action?: CopilotAction;
  compact?: boolean;
  disabled?: boolean;
  iconProps?: IIconProps;
  maxWidth?: number;
  minWidth?: number;
  processSelectionRequired?: boolean;
  selected?: boolean;
  suppressProcessStartIcon?: boolean;
  text?: string;
  width?: number | string;
  onActionClick?: (item: CopilotAction) => void;
  onClick?: () => void;
};

export const CopilotPanelButton = (props: CopilotPanelButtonProps) => {
  const theme = useTheme();
  const defaultBackgroundColor = theme.semanticColors.buttonBackground;
  const startableBackgroundColor = theme.semanticColors.successBackground;
  const selectedBackgroundColor = theme.palette.themePrimary;
  const selectedRunningProcessBackgroundColor = theme.semanticColors.accentTintBackground;
  const selectedRunningProcessHoverBackgroundColor = theme.semanticColors.accentTintBackgroundHovered;
  const disabledBackgroundColor = theme.semanticColors.disabledBackground;
  const defaultTextColor = theme.semanticColors.buttonText;
  const selectedTextColor = theme.palette.white;
  const disabledTextColor = theme.semanticColors.disabledText;
  const isDisabled = props.disabled || props.action?.disabled;
  const text = props.text ?? props.action?.name ?? '';
  const minHeight = props.compact ? 28 : 34;
  const isProcessAction = props.action?.type === CopilotActionType.Process;
  const status = props.action?.status ?? TenantProcessStatus.NotStarted;
  // a button whose first click only selects its process; a finished process starts at once, so it looks startable
  const isUnselectedProcessSelector =
    isProcessAction && props.processSelectionRequired === true && !props.selected && !isFinishedProcessStatus(status);
  const isSelectedRunningProcess = isProcessAction && props.selected && status === TenantProcessStatus.InProgress;
  const isRestartableProcessStatus =
    status === TenantProcessStatus.NotStarted ||
    status === TenantProcessStatus.Cancelled ||
    status === TenantProcessStatus.Completed ||
    status === TenantProcessStatus.Failed;

  const getProcessBorderColor = (): string => {
    if (isUnselectedProcessSelector) return theme.semanticColors.buttonBorder;
    if (isRestartableProcessStatus) return theme.semanticColors.successIcon;

    switch (status) {
      case TenantProcessStatus.InProgress:
        return theme.palette.themePrimary;
      default:
        return theme.semanticColors.buttonBorder;
    }
  };

  const getProcessBackgroundColor = (): string => {
    if (isUnselectedProcessSelector) return defaultBackgroundColor;
    if (isSelectedRunningProcess) return selectedRunningProcessBackgroundColor;
    if (isRestartableProcessStatus) return startableBackgroundColor ?? defaultBackgroundColor;

    switch (status) {
      case TenantProcessStatus.InProgress:
      default:
        return defaultBackgroundColor;
    }
  };

  const getProcessHoverBackgroundColor = (): string => {
    if (isSelectedRunningProcess) return selectedRunningProcessHoverBackgroundColor;

    if (isRestartableProcessStatus) {
      return startableBackgroundColor ?? defaultBackgroundColor;
    }

    return theme.semanticColors.buttonBackgroundHovered ?? defaultBackgroundColor;
  };

  const backgroundColor =
    props.action?.type === CopilotActionType.Process
      ? getProcessBackgroundColor()
      : props.selected
      ? selectedBackgroundColor
      : defaultBackgroundColor;
  const hoverBackgroundColor =
    props.action?.type === CopilotActionType.Process
      ? getProcessHoverBackgroundColor()
      : theme.semanticColors.buttonBackgroundHovered ?? defaultBackgroundColor;
  const borderColor = isProcessAction
    ? getProcessBorderColor()
    : props.selected
    ? selectedBackgroundColor
    : 'transparent';
  const textColor =
    props.action?.type !== CopilotActionType.Process && props.selected ? selectedTextColor : defaultTextColor;

  function getActionIconProps(): IIconProps | undefined {
    if (!props.action || props.action.type !== CopilotActionType.Process) return props.iconProps;

    if (status === TenantProcessStatus.InProgress) {
      return stopIcon;
    }
    if (props.suppressProcessStartIcon) return undefined;
    if (isUnselectedProcessSelector) return undefined;

    return startIcon;
  }

  const actionIconProps = getActionIconProps();
  const hasIcon = Boolean(props.iconProps) || Boolean(actionIconProps);

  const onButtonClick = () => {
    if (isDisabled) return;

    if (!props.action) {
      props.onClick?.();

      return;
    }

    props.onActionClick?.(props.action);
  };

  const getGradientBackground = (innerBackgroundColor: string): string =>
    `linear-gradient(${innerBackgroundColor}, ${innerBackgroundColor}) padding-box, linear-gradient(105deg, #2f80ff 0%, #25c26e 32%, #ffd43b 58%, #ff6b35 78%, #ff3d57 100%) border-box`;

  return (
    <IPDefaultButton
      disabled={isDisabled}
      iconProps={actionIconProps}
      text={text}
      title={text}
      onClick={onButtonClick}
      styles={{
        root: {
          background: getGradientBackground(backgroundColor),
          border: '1px solid transparent',
          borderRadius: 6,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          color: textColor,
          cursor: isDisabled ? undefined : 'pointer',
          height: minHeight,
          minHeight,
          minWidth: props.minWidth,
          maxWidth: props.maxWidth,
          padding: props.compact ? '0 8px' : '0 12px',
          width: props.width,
        },
        rootHovered: {
          background: getGradientBackground(hoverBackgroundColor),
          border: '1px solid transparent',
          color: textColor,
        },
        rootPressed: {
          background: getGradientBackground(hoverBackgroundColor),
          border: '1px solid transparent',
          color: textColor,
        },
        rootDisabled: {
          background: getGradientBackground(disabledBackgroundColor),
          border: '1px solid transparent',
          color: disabledTextColor,
        },
        icon: {
          color: isDisabled
            ? disabledTextColor
            : isProcessAction && !isSelectedRunningProcess
            ? borderColor
            : textColor,
          fontSize: props.compact ? 12 : 14,
        },
        iconDisabled: {
          color: disabledTextColor,
        },
        label: {
          color: textColor,
          fontWeight: globalFontBoldWeight,
          margin: hasIcon ? '0 0 0 6px' : undefined,
          whiteSpace: 'nowrap',
        },
        labelDisabled: {
          color: disabledTextColor,
        },
      }}
    />
  );
};
